'use client';

import { c } from '../../../utils/tailwind-merge';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { IconButton } from '../../atoms/Button/IconButton';
import { ConfirmationPopup } from '../../molecules/ConfirmationPopup';
import { FavoriteListModal } from '../FavoriteListModal';
import { ShareListModal } from '../ShareListModal';
import type {
  ProductListHeaderProps,
  ProductListHeaderPropsExisting,
  ProductListHeaderPropsNew,
  ProductListHeaderPropsSeeMore,
} from './ProductListHeader.types';
import { ProductListSeeMoreLink } from './ProductListSeeMoreLink';

const titleSizeClasses = {
  lg: 'text-3xl md:text-3xl lg:text-5xl font-bold',
  xl: 'text-4xl md:text-4xl lg:text-7xl font-bold',
};

const renderNewActions = (props: ProductListHeaderPropsNew) => {
  const { microcopy, favoriteListModalProps } = props;

  return (
    <FavoriteListModal
      {...favoriteListModalProps}
      trigger={
        <ButtonV2
          variant="secondary-blue-monochrome"
          size="large"
          shape="rounded"
          label={microcopy.new}
          iconLeft="Plus"
          iconWeight="bold"
          iconSize={undefined}
          iconClassName="h-5 w-5 !mr-1.5"
          aria-label={microcopy.newAria}
          data-test="product-list-header-new-button"
          className="px-3 py-2 text-sm md:text-base lg:px-4 lg:py-[14px]"
        />
      }
    />
  );
};

const responsiveIconClasses =
  'h-5 w-5 lg:h-6 lg:w-6 fill-grey-800 group-hover:fill-blue';

const renderExistingActions = (props: ProductListHeaderPropsExisting) => {
  const {
    microcopy,
    favoriteListModalProps,
    confirmationPopupProps,
    shareListModalProps,
  } = props;

  return (
    <div className="flex items-center gap-2">
      <ConfirmationPopup
        {...confirmationPopupProps}
        trigger={
          <IconButton
            iconName="Trash"
            ariaLabel={microcopy.deleteAria}
            iconWeight="bold"
            iconClassName={responsiveIconClasses}
            className="group w-auto px-2 md:p-3"
            testId="product-list-header-delete-button"
          />
        }
      />
      <FavoriteListModal
        {...favoriteListModalProps}
        trigger={
          <IconButton
            iconName="PencilSimple"
            ariaLabel={microcopy.editAria}
            iconWeight="bold"
            iconClassName={responsiveIconClasses}
            className="group w-auto px-2 md:p-3"
            testId="product-list-header-edit-button"
          />
        }
      />
      <ShareListModal
        {...shareListModalProps}
        trigger={
          <IconButton
            iconName="ShareNetwork"
            ariaLabel={microcopy.shareAria}
            iconWeight="bold"
            iconClassName={responsiveIconClasses}
            className="group w-auto px-2 md:p-3"
            testId="product-list-header-share-button"
          />
        }
      />
    </div>
  );
};

const renderSeeMoreActions = (props: ProductListHeaderPropsSeeMore) => {
  const { microcopy, href, name } = props;

  return (
    <ProductListSeeMoreLink
      href={href}
      name={name}
      microcopy={microcopy}
      className="hidden md:flex"
      testId="product-list-header-see-more-link"
    />
  );
};

export const ProductListHeader = (props: ProductListHeaderProps) => {
  const { className = '', size, name, type } = props;

  const renderActions = () => {
    switch (type) {
      case 'new':
        return renderNewActions(props);
      case 'existing':
        return renderExistingActions(props);
      case 'seeMore':
        return renderSeeMoreActions(props);
      case 'default':
      default:
        return null;
    }
  };

  return (
    <header
      className={c(
        'flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between',
        className
      )}
      data-test="product-list-header"
    >
      <h2 className={c(titleSizeClasses[size], 'text-grey-900')}>{name}</h2>
      {renderActions()}
    </header>
  );
};

ProductListHeader.displayName = 'ProductListHeader';
